import { NAMESPACE, useLang } from '@/hooks/use-lang';
import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import '../index.scss';

// import required modules
import { FreeMode, Navigation } from 'swiper/modules';
import { Button, Skeleton } from '@/shared/components';
import Link from 'next/link';
import { usePriceFormat } from '@/hooks/use-price-format';
import ThermoDefaultImg from '@/assets/images/cart/Thermo Fisher Scientific.svg';
import { ProductImage } from '@/shared/pageComponents';
import { useQueryRecommendedProducts } from '@/hooks/product-detail.hook';
import { useProductClickEventTracking } from '@/shared/tracking/use-tracking-helpers';
import { buildPdpLink } from '@/app/utils/build-pdp-link';
import { cartService } from '@/services/cart.service';
import { useCountry } from '@/hooks/use-country';

interface IProps {
  className?: string;
  catalogNumber: string;
}

export const RecommendedProducts = (props: IProps) => {
  const { t, lang } = useLang(NAMESPACE.pdp);
  const { isKR } = useCountry();
  const priceFormat = usePriceFormat();
  const { data, isLoading } = useQueryRecommendedProducts(props.catalogNumber);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availability, setAvailability] = useState<any>({});
  const productClickEventTracking = useProductClickEventTracking();
  const height = isKR ? 'h-[483px]' : 'h-[433px]';
  useEffect(() => {
    if (isKR && data && data.length > 0) {
      setAvailabilityLoading(true);
      const catalogParam = data.map((item) => ({
        catalogNumber: item.catalogNumber,
        requestQuantity: 1,
      }));
      cartService
        .getProductsBatchAvailability(catalogParam)
        .then((res) => {
          if (res.data && res.data.length > 0) {
            const newAvailability = {};
            res.data.forEach((item) => {
              if (item?.availabilityMessage) {
                newAvailability[item.catalogNumber] = item.availabilityMessage;
              }
            });
            setAvailability(newAvailability);
          }
          setAvailabilityLoading(false);
        })
        .catch((error) => {
          setAvailabilityLoading(false);
          console.error('Error fetching availability:', error);
        });
    }
  }, [data]);
  return (
    <>
      {data && data.length > 0 && (
        <div className={props.className}>
          <h2>{t('recommended.products')}</h2>
          <div className="w-full h-[1px] bg-gray-400 mt-4 mb-5" />
          <div className="flex gap-x-4 pdp-swiper-container">
            <div
              className={`w-[60px] px-4 ${height}  border border-solid border-gray-450 flex items-center justify-center cursor-pointer swiper-prev-button`}
            >
              <i className="icon-24-chevron-left-bold text-blue-500" />
            </div>
            <Swiper
              slidesPerView={'auto'}
              spaceBetween={16}
              freeMode={true}
              loop={false}
              className="flex-1 overflow-hidden"
              navigation={{
                enabled: true,
                nextEl: '.swiper-next-button',
                prevEl: '.swiper-prev-button',
              }}
              modules={[Navigation, FreeMode]}
            >
              {isLoading &&
                [1, 2, 3].map((item, index) => (
                  <SwiperSlide key={index} className="w-[365px]">
                    <div
                      className={`${height} border border-solid border-gray-450 shadow-sm flex flex-col px-10 sm:px-4 py-8 bg-white`}
                    >
                      <Skeleton height={'155px'} width={'212px'} className="w-1/2 h-2/5 self-center" rows={1} />
                      <Skeleton height="20px" rows={1} className="font-bold mt-4 line-clamp-2" />
                      <div className="flex mt-4 gap-x-1">
                        <Skeleton height="20px" width="140px" rows={1} />
                      </div>
                      <div className="flex text-sm mt-6 gap-x-1 items-center">
                        <Skeleton rows={1} />
                      </div>
                      <div className="flex text-sm mt-6 gap-x-1 items-center">
                        <Skeleton height={'45px'} rows={1} />
                      </div>
                      <Button type="outline" className="mt-8" disabled>
                        {t('view.product.detail')}
                      </Button>
                    </div>
                  </SwiperSlide>
                ))}
              {!isLoading &&
                data.map((item, index) => (
                  <SwiperSlide key={index} className="w-[365px]">
                    <div
                      className={`${height} border border-solid border-gray-450 shadow-sm flex flex-col px-10 sm:px-4 py-8 bg-white`}
                    >
                      <Link
                        href={buildPdpLink(item.catalogNumber)}
                        prefetch
                        className="flex justify-center h-2/5 w-full"
                      >
                        <ProductImage
                          width={212}
                          height={155}
                          className="self-center object-contain"
                          src={item.productImage ?? ThermoDefaultImg}
                          bordered={false}
                          onClick={() => {
                            productClickEventTracking(item, index);
                          }}
                        />
                      </Link>
                      <Link
                        onClick={(e) => {
                          productClickEventTracking(item, index);
                        }}
                        href={buildPdpLink(item.catalogNumber)}
                        prefetch
                        className="font-bold mt-4 line-clamp-2 h-11"
                        dangerouslySetInnerHTML={{ __html: item.productTitle }}
                      />
                      <div>
                        {t('catalog.number')}: {item.recommendRootSku}
                      </div>
                      <div className="flex text-sm mt-6 gap-x-1 items-center">
                        <span className="text-2xl font-medium sm:text-sm">{priceFormat(item.bestPrice)}</span>
                        <span>/</span>
                        <span dangerouslySetInnerHTML={{ __html: item.unitSize }} />
                      </div>
                      {isKR && availabilityLoading && (
                        <div className="flex text-sm mt-6 gap-x-1 items-center">
                          <Skeleton height={'45px'} rows={1} />
                        </div>
                      )}
                      {isKR && !availabilityLoading && (
                        <div className="h-[45px]">
                          {availability[item.catalogNumber] && availability[item.catalogNumber][lang.toUpperCase()]}
                        </div>
                      )}
                      <Link prefetch href={buildPdpLink(item.catalogNumber)} className="mt-8 flex">
                        <Button
                          type="outline"
                          className="flex-1"
                          onClick={() => {
                            productClickEventTracking(item, index);
                          }}
                        >
                          {t('view.product.detail')}
                        </Button>
                      </Link>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
            <div
              className={`w-[60px] px-4 ${height}  border border-solid border-gray-450 flex items-center justify-center cursor-pointer swiper-next-button`}
            >
              <i className="icon-24-chevron-right-bold text-blue-500" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
